/**
 * Import Configuration Index Page
 *
 * Main page for managing bank transaction import from Google Sheets.
 * Shows configuration status, allows setup/editing, and provides sync controls.
 *
 * States:
 * - Google Not Connected: Shows warning, link to Settings
 * - Not Configured: Shows setup form
 * - Configured: Shows config details, edit button, sync controls
 */

import { useState, useCallback, useEffect } from 'react';
import { Head, usePage, Link } from '@inertiajs/react';
import { Toaster } from 'sonner';
import { Navbar } from '@/components';

import type { ImportConfig, FailedImportsData } from '@/types/accounting';
import type { SharedProps } from '@/types';
import type ApiAccountingCsvImportLog from '@/types/serializers/ApiAccountingCsvImportLog';

import { useImportConfig } from '@/hooks/useImportConfig';
import { ImportConfigForm } from './components/ImportConfigForm';
import { SyncButton } from './components/SyncButton';
import { showSyncResultToast } from './components/SyncResultToast';
import { FailedImportsSection } from './components/FailedImportsSection';
import { ReapplyWorkflowSection } from '../Settings/ReapplyWorkflowSection';
import { CsvImportSection } from './components/CsvImportSection';
import { ImportHistorySection } from './components/ImportHistorySection';

/**
 * Props for the Import Configuration page
 */
interface ImportPageProps extends SharedProps {
  importConfig: ImportConfig;
  googleConnected: boolean;
  failedImports: FailedImportsData;
  pendingImportsCount: number;
}

/**
 * ImportIndex is the main page component for import configuration management.
 */
export default function ImportIndex() {
  // Get page props from Inertia
  const { importConfig: initialConfig, googleConnected, failedImports, pendingImportsCount: initialPendingCount } =
    usePage<ImportPageProps>().props;

  // Use the import config hook for API operations
  const {
    config,
    loading,
    sync,
    testConnection,
    createConfig,
    updateConfig,
    deleteConfig,
  } = useImportConfig();

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(initialPendingCount);
  const [isProcessingPending, setIsProcessingPending] = useState(false);
  const [newCsvImports, setNewCsvImports] = useState<ApiAccountingCsvImportLog[]>([]);

  // Initialize config from server props
  useEffect(() => {
    // The hook's config will be null until we fetch, so use initialConfig
  }, [initialConfig]);

  // Use config from hook if available, otherwise use initial props
  const currentConfig = config ?? initialConfig;
  const isConfigured = currentConfig.configured;

  // Handle sync
  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const result = await sync();
      showSyncResultToast(result);
    } catch (error) {
      // Error is already handled by the hook and showSyncResultToast
    } finally {
      setIsSyncing(false);
    }
  }, [sync]);

  // Handle delete configuration
  const handleDelete = useCallback(async () => {
    if (!confirm('Are you sure you want to remove this import configuration? This cannot be undone.')) {
      return;
    }

    try {
      await deleteConfig();
      setIsEditing(false);
    } catch (error) {
      // Error is already handled by the hook
    }
  }, [deleteConfig]);

  // Handle form submission
  const handleFormSubmit = useCallback(async () => {
    setIsEditing(false);
  }, []);

  // Handle form cancel
  const handleFormCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  // Handle CSV import completion
  const handleCsvImportComplete = useCallback((log: ApiAccountingCsvImportLog) => {
    setNewCsvImports((prev) => [log, ...prev]);
  }, []);

  // Handle process pending imports
  const handleProcessPending = useCallback(async () => {
    setIsProcessingPending(true);
    try {
      const response = await fetch('/api/accounting/import_config/process_pending', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      });
      const data = await response.json();
      if (data.success) {
        setPendingCount(0);
      }
    } catch (error) {
      console.error('Failed to process pending imports:', error);
    } finally {
      setIsProcessingPending(false);
    }
  }, []);

  // Format last sync time
  const formatLastSync = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  // Truncate spreadsheet ID for display
  const truncateSpreadsheetId = (id: string) => {
    if (id.length <= 24) return id;
    return `${id.substring(0, 12)}...${id.substring(id.length - 12)}`;
  };

  return (
    <>
      <Head title="Import Configuration" />
      <Toaster position="top-right" richColors closeButton />

      <div className="min-h-screen bg-gray-50" data-testid="import-config-page">
        <Navbar currentSection="accounting" />

        {/* Header */}
        <header className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Import Configuration</h1>
              <p className="mt-1 text-sm text-gray-500">
                Automatically import bank transactions from Google Sheets
              </p>
            </div>
            <nav className="flex items-center gap-2">
              <Link
                href="/accounting/transactions"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Transactions
              </Link>
            </nav>
          </div>
        </header>

        <main className="px-6 py-6 max-w-4xl mx-auto">
          {/* Google Not Connected Warning */}
          {!googleConnected && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <svg
                className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-amber-800">Google Account Not Connected</h3>
                <p className="text-sm text-amber-700 mt-1">
                  You need to connect your Google account before you can import from Google Sheets.{' '}
                  <Link href="/settings" className="font-medium underline hover:no-underline">
                    Go to Settings
                  </Link>
                </p>
              </div>
            </div>
          )}

          {/* Not Configured - Empty State */}
          {!isConfigured && !isEditing && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-16 text-center">
                <svg
                  className="w-16 h-16 text-gray-300 mx-auto mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h2 className="text-lg font-semibold text-gray-700 mb-2">No Import Configured</h2>
                <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                  Connect a Google Sheet to automatically import your bank transactions.
                  Your spreadsheet should contain transaction data from services like Wise or Tiller.
                </p>
                <button
                  onClick={() => setIsEditing(true)}
                  disabled={!googleConnected}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                  </svg>
                  Configure Import
                </button>
              </div>
            </div>
          )}

          {/* Editing / Creating Form */}
          {isEditing && (
            <ImportConfigForm
              initialConfig={isConfigured ? currentConfig : undefined}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              onTestConnection={testConnection}
              createConfig={createConfig}
              updateConfig={updateConfig}
            />
          )}

          {/* Configured - Display State */}
          {isConfigured && !isEditing && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Card Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-900">Bank Import</span>
                  {currentConfig.active ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <SyncButton
                    onSync={handleSync}
                    isLoading={isSyncing}
                    disabled={!googleConnected}
                  />
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                      />
                    </svg>
                    Edit
                  </button>
                </div>
              </div>

              {/* Card Body */}
              <div className="px-6 py-5">
                {/* Info Grid */}
                <div className="grid grid-cols-3 gap-6 mb-6">
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      Spreadsheet ID
                    </span>
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded text-gray-700 inline-block break-all">
                      {truncateSpreadsheetId(currentConfig.spreadsheetId || '')}
                    </span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      Sheet Name
                    </span>
                    <span className="text-sm text-gray-900">{currentConfig.sheetName}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      Last Synced
                    </span>
                    <span className="text-sm text-gray-900">
                      {formatLastSync(currentConfig.lastSyncAt)}
                      {currentConfig.lastRow && currentConfig.lastRow > 1 && (
                        <span className="text-gray-500"> (row {currentConfig.lastRow.toLocaleString()})</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Remove Configuration
                </button>
              </div>
            </div>
          )}

          {/* Failed Imports Section - only shown when configured and has failures */}
          {isConfigured && !isEditing && (
            <FailedImportsSection failedImports={failedImports} />
          )}

          {/* Pending Imports Section - only shown when there are pending imports */}
          {pendingCount > 0 && !isEditing && (
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">
                      {pendingCount} Pending Import{pendingCount !== 1 ? 's' : ''}
                    </h3>
                    <p className="text-sm text-amber-700">
                      Raw rows waiting to be processed into transactions
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleProcessPending}
                  disabled={isProcessingPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessingPending ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Process Now
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Reapply Workflow Section - only shown when configured and not editing */}
          {isConfigured && !isEditing && (
            <div className="mt-6">
              <ReapplyWorkflowSection />
            </div>
          )}

          {/* CSV File Import Section */}
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">CSV File Import</h2>
            <CsvImportSection onImportComplete={handleCsvImportComplete} />
          </div>

          {/* Import History Section */}
          <div className="mt-6 mb-8">
            <ImportHistorySection newImports={newCsvImports} />
          </div>
        </main>
      </div>
    </>
  );
}
